import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import _ from 'lodash';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import Utils from 'general/utils/Utils';

SetKPIView.propTypes = {
    item: PropTypes.object,
    onSubmit: PropTypes.func,
    isDefaultKPI: PropTypes.bool,
};

const ref = createRef();

function SetKPIView(props) {
    const { item, onSubmit, isDefaultKPI = true } = props;
    const refFormik = useRef();
    const { t } = useTranslation();
    const [totalKPI, setTotalKPI] = useState(0);
    const [readyToValidData, setReadyToValidData] = useState(false);
    const currentMonth = moment().format('YYYY-MM');

    const monthKPIValues = useMemo(() => {
        const findValue = (type) =>
            item?.kpis?.find(
                (kpi) =>
                    kpi?.orderType === type &&
                    kpi?.month === currentMonth,
            )?.kpiValue ?? 0;
        return {
            saleKPI: findValue('INVESTOR'),
            packageKPI: findValue('GROUP'),
            chargePointKPI: findValue('CHARGEPOINT'),
        };
    }, [item, currentMonth]);

    const initialValues = useMemo(() => {
        return {
            saleKPI: isDefaultKPI
                ? item?.saleKPI ?? 0
                : monthKPIValues.saleKPI ?? 0,
            coinvestorKPI: isDefaultKPI
                ? item?.coinvestorKPI ?? 0
                : monthKPIValues.coinvestorKPI ?? 0,
            packageKPI: isDefaultKPI
                ? item?.packageKPI ?? 0
                : monthKPIValues.packageKPI ?? 0,
            chargePointKPI: isDefaultKPI
                ? item?.chargePointKPI ?? 0
                : monthKPIValues.chargePointKPI ?? 0,
        };
    }, [item, isDefaultKPI, monthKPIValues]);

    useEffect(() => {
        const { saleKPI, packageKPI, chargePointKPI } = initialValues;
        setTotalKPI(Number(saleKPI) + Number(packageKPI) + Number(chargePointKPI));
    }, [initialValues]);

    useEffect(() => {
        ref.current = {
            onSubmit: async () => {
                setReadyToValidData(true);

                const errors = await refFormik.current?.validateForm();

                if (_.isEmpty(errors)) {
                    refFormik.current?.handleSubmit();
                }
            },
        };
    }, []);

    const handleChangeKPI = (field, value, formikValues) => {
        const updated = {
            ...formikValues,
            [field]: Number(value ?? 0),
        };

        const newTotal =
            Number(updated.saleKPI ?? 0) +
            Number(updated.coinvestorKPI ?? 0) +
            Number(updated.packageKPI ?? 0) +
            Number(updated.chargePointKPI ?? 0);

        setTotalKPI(newTotal);
    };

    const kpiValidation = Yup.number()
        .transform((value, originalValue) =>
            originalValue === '' ? undefined : Number(originalValue),
        )
        .required(t('required'))
        .test(
            'greater-than-zero',
            'KPI phải lớn hơn 0',
            (value) => {
                if (!Utils.isValid(value)) return false;
                return value > 0;
            },
        );

    return (
        <div className="w-100">
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={Yup.object({
                    saleKPI: kpiValidation,
                    coinvestorKPI: kpiValidation,
                    packageKPI: kpiValidation,
                    chargePointKPI: kpiValidation,
                })}
                onSubmit={(values) => {
                    if (onSubmit) {
                        onSubmit(values);
                    }
                }}
            >
                {(formikProps) => {
                    refFormik.current = formikProps;

                    const renderInput = (name, label) => (
                        <div className="col-12 p-0 mt-4">
                            <KTFormGroup
                                label={<span className="font-weight-bolder">{label} </span>}
                                inputName={name}
                                additionalClassName="mb-0"
                                inputElement={
                                    <Field name={name}>
                                        {({ field, form, meta }) => (
                                            <KTFormInput
                                                name={field.name}
                                                value={field.value}
                                                onChange={(value) => {
                                                    form.setFieldValue(field.name, value);
                                                    handleChangeKPI(field.name, value, formikProps.values);
                                                }}
                                                onBlur={() => {
                                                    form.setFieldTouched(field.name, true);
                                                }}
                                                enableCheckValid
                                                type={KTFormInputType.currency}
                                                placeholder={t('enterNumber')}
                                                isValid={!readyToValidData || _.isEmpty(meta.error)}
                                                isTouched={readyToValidData}
                                                feedbackText={readyToValidData ? meta.error : ''}
                                            />
                                        )}
                                    </Field>
                                }
                                isRequired={true}
                            />
                        </div>
                    );

                    return (
                        <div className="row m-0 w-100">
                            {renderInput('packageKPI', t('KPI bán gói đầu tư HTKD'))}
                            {renderInput('saleKPI', t('KPI bán nhượng quyền'))}
                            {renderInput('chargePointKPI', t('KPI bán trụ sạc'))}

                            <div className="col-12 p-0 mt-4">
                                <KTFormGroup
                                    label={<span className="font-weight-bolder">{t('Tổng mức KPI thiết lập')} </span>}
                                    inputName="totalKPI"
                                    additionalClassName="mb-0"
                                    inputElement={
                                        <KTFormInput
                                            name="totalKPI"
                                            value={totalKPI}
                                            onChange={() => { }}
                                            enableCheckValid
                                            type={KTFormInputType.currency}
                                            placeholder={t('enterNumber')}
                                            isValid={true}
                                            disabled={true}
                                        />
                                    }
                                />
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </div>
    );
}
export default SetKPIView;
export const SetKPIViewHelper = {
    onSubmit: () => {
        ref.current.onSubmit();
    },
};
